import useUpdateProjectCampaign from '../../hooks/useUpdateProjectCampaign';
import { creatorbaseQueries } from '@round/api';
import { Modal } from '@round/whitelabel-ui';
import { Button } from '@round/whitelabel-ui';
import { FormField } from '@round/whitelabel-ui';
import styles from './CampaignBriefModal.module.css';
import { Form, FormikProvider } from 'formik';
import { useFormik } from 'formik';
import { getObjectDifference, showNotification } from 'helpers';
import pick from 'lodash/pick';
import * as yup from 'yup';
import { mapApiErrorsToFormikErrors } from 'utility/utility';
import { CampaignTableRow } from '../ProjectCampaignsTable/ProjectCampaignsTable';

type Props = {
    isOpen: boolean;
    closeModal: () => void;
    campaign: CampaignTableRow | null;
};

const CampaignBriefModal = ({ isOpen, closeModal, campaign }: Props) => {
    const { update: updateCampaign } = useUpdateProjectCampaign();
    const formik = useFormik({
        initialValues: {
            brief: campaign?.brief || '',
            brief_turnaround: campaign?.brief_turnaround || '',
        },
        validationSchema: yup.object({
            brief: yup.string(),
            brief_turnaround: yup.string(),
        }),
        onSubmit: (values, helpers) => {
            if (!campaign?.id) {
                showNotification('Could not update brief', 'error');
                helpers.setSubmitting(false);
                return;
            }

            const updatedFields = getObjectDifference(values, campaign);

            updateCampaign(
                { id: campaign.id, ...updatedFields },
                {
                    onSuccess: () => {
                        showNotification('Brief updated', 'info');
                        helpers.resetForm();
                        closeModal();
                    },
                    onError: (error) => {
                        if (creatorbaseQueries.campaigns.utils.update.isExpectedErrorResponse(error)) {
                            const { response } = error;
                            if (response.status === 400) {
                                helpers.setErrors(
                                    pick(mapApiErrorsToFormikErrors(pick(response.data, ['brief', 'brief_turnaround'])))
                                );
                                return;
                            }
                            if (response.status === 404) {
                                showNotification('Campaign not found', 'error');
                                return;
                            }
                        }

                        showNotification('Could not update brief', 'error');
                    },
                    onSettled: () => {
                        helpers.setSubmitting(false);
                    },
                }
            );
        },
        enableReinitialize: true,
    });
    const handleClose = () => {
        formik.resetForm();
        closeModal();
    };
    return (
        <Modal className={styles.modal} closeOnOverlayClick isOpen={isOpen} onClose={handleClose}>
            <Modal.Header>
                <Modal.Title>Edit Brief</Modal.Title>
                <span className={styles.subTitle}>{campaign?.name}</span>
            </Modal.Header>
            <Modal.Body>
                <FormikProvider value={formik}>
                    <Form className={styles.form}>
                        <FormField
                            name="brief"
                            component="textarea"
                            label="Campaign Brief"
                            placeholder="Enter the campaign brief"
                            disabled={formik.isSubmitting}
                            className={styles.briefInput}
                        />

                        <FormField
                            name="brief_turnaround"
                            label="Turnaround"
                            placeholder="Enter the turnaround time for the brief"
                            disabled={formik.isSubmitting}
                        />
                    </Form>
                </FormikProvider>
            </Modal.Body>
            <Modal.Actions>
                <Button
                    appearance="primary"
                    disabled={!formik.dirty || !formik.isValid}
                    isLoading={formik.isSubmitting}
                    onClick={formik.submitForm}
                >
                    Submit
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default CampaignBriefModal;
